import { useCallback, useEffect, useMemo } from 'react'
import { AxiosError } from 'axios'
import qs from 'qs'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import { ApiErrorResponse, UserResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import type {
  GetCertificatesSortColumn,
  GetCertificatesFilterModelDto,
  SortingOrder,
  GetCertificatesPaginatedResponse,
  CertificateReviewRequest,
} from '@tabeeb/contracts/microservices/swagger-certificate-service'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'
import { useApi } from '@tabeeb/modules/shared/utils/API/hooks'

import { CertificatesQueryKeys } from '../queries'
import { CertificatesDisplayType } from '../constants'
import type { Certificate } from '../types'
import { Scope } from '../enums'

type GetAllWithPaginationParams = {
  Search?: string
  'Sort.Field'?: GetCertificatesSortColumn
  'Sort.SortOrder'?: SortingOrder
  FilterModels?: GetCertificatesFilterModelDto[]
  PageNumber?: number
  PageSize?: number
  contractorSourceTenantId?: number
  userGuid?: string
  userId?: number
  contractVersionId?: number
}

export interface UseCertificatesGridProps {
  search?: GetAllWithPaginationParams['Search']
  scope?: Scope
  sorting?: {
    field: GetAllWithPaginationParams['Sort.Field']
    order: GetAllWithPaginationParams['Sort.SortOrder']
  } | null
  filtering?: GetAllWithPaginationParams['FilterModels']
  pageNumber: NonNullable<GetAllWithPaginationParams['PageNumber']>
  pageSize: NonNullable<GetAllWithPaginationParams['PageSize']>
  contractorSourceTenantId?: GetAllWithPaginationParams['contractorSourceTenantId']
  userGuid?: GetAllWithPaginationParams['userGuid']
  userId?: GetAllWithPaginationParams['userId']
  contractVersionId?: GetAllWithPaginationParams['contractVersionId']
  displayType?: CertificatesDisplayType
}

const useCertificates = ({
  search,
  scope = Scope.Tenant,
  sorting,
  filtering,
  pageNumber,
  pageSize,
  contractorSourceTenantId,
  userGuid,
  userId,
  contractVersionId,
  displayType = CertificatesDisplayType.All,
}: UseCertificatesGridProps) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  const { certificateServiceApi: api, mainApi } = useApi()

  const isPersonalCertificates = useMemo(() => displayType === CertificatesDisplayType.Personal, [displayType])

  const certificatesQuery = useQuery({
    queryKey: [
      CertificatesQueryKeys.Certificates,
      displayType,
      isPersonalCertificates,
      search,
      scope,
      sorting?.field,
      sorting?.order,
      pageNumber,
      pageSize,
      filtering,
      contractorSourceTenantId,
      userGuid,
      userId,
      contractVersionId,
    ],
    queryFn: async () => {
      let certificatesResponse: GetCertificatesPaginatedResponse

      if (isPersonalCertificates) {
        certificatesResponse = await api.certificates.getMyWithPaginationAsync(
          {
            Search: search,
            Scope: scope,
            'Sort.Field': sorting?.field,
            'Sort.SortOrder': sorting?.order,
            FilterModels: filtering,
            PageNumber: pageNumber,
            PageSize: pageSize,
          },
          {
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          }
        )
      } else if (displayType === CertificatesDisplayType.Contract && contractVersionId != null) {
        certificatesResponse = await api.certificates.getForContractedTenantAsync({
          ContractVersionId: contractVersionId,
          Search: search,
          Scope: scope,
          'Sort.Field': sorting?.field,
          'Sort.SortOrder': sorting?.order,
          FilterModels: filtering,
          PageNumber: pageNumber,
          PageSize: pageSize,
        })
      } else if (displayType === CertificatesDisplayType.ContractUser && contractorSourceTenantId != null && userGuid) {
        certificatesResponse = await api.certificates.getForContractUserAsync(
          {
            ContractorSourceTenantId: contractorSourceTenantId,
            UserGuid: userGuid,
            Search: search,
            Scope: scope,
            'Sort.Field': sorting?.field,
            'Sort.SortOrder': sorting?.order,
            FilterModels: filtering,
            PageNumber: pageNumber,
            PageSize: pageSize,
          },
          {
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          }
        )
      } else if (displayType === CertificatesDisplayType.User && userGuid && userId != null) {
        certificatesResponse = await api.certificates.getForUserAsync(
          {
            UserGuid: userGuid,
            UserId: userId,
            Search: search,
            Scope: scope,
            'Sort.Field': sorting?.field,
            'Sort.SortOrder': sorting?.order,
            FilterModels: filtering,
            PageNumber: pageNumber,
            PageSize: pageSize,
          },
          {
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          }
        )
      } else {
        certificatesResponse = await api.certificates.getAllWithPaginationAsync(
          {
            Search: search,
            Scope: scope,
            'Sort.Field': sorting?.field,
            'Sort.SortOrder': sorting?.order,
            FilterModels: filtering,
            PageNumber: pageNumber,
            PageSize: pageSize,
          },
          {
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          }
        )
      }

      const ownerGuids = new Set<string>()
      for (const certificate of certificatesResponse.Certificates) {
        if (certificate.OwnerIdentityGuid) {
          ownerGuids.add(certificate.OwnerIdentityGuid)
        }
      }

      let owners: UserResponse[] = []
      if (!isPersonalCertificates && ownerGuids.size > 0) {
        const ownersResponse = await mainApi.users.getUsersByIdentityGuidsAsync(
          {
            IdentityGuids: Array.from(ownerGuids),
          },
          {
            paramsSerializer: (params) => qs.stringify(params, { allowDots: true }),
          }
        )
        owners = ownersResponse ?? []
      }

      const certificatesWithOwners: Certificate[] = certificatesResponse.Certificates.map((certificate) => ({
        ...certificate,
        Owner: owners.find((u) => u.IdentityGuid === certificate.OwnerIdentityGuid),
      }))

      return {
        Certificates: certificatesWithOwners,
        TotalCount: certificatesResponse.TotalCount,
      }
    },
  })

  const { isError: isCertificatesError, error: certificatesError } = certificatesQuery
  useEffect(() => {
    if (isCertificatesError && certificatesError) {
      const error = certificatesError as AxiosError<ApiErrorResponse>
      const message = getErrorMessageFromResponse(error.response?.data)
      dispatch(onAddErrorNotification({ message }))
    }
  }, [isCertificatesError, certificatesError, dispatch])

  const updateMutation = useMutation({
    mutationFn: api.certificates.updateAsync,
    onError: (error: AxiosError<ApiErrorResponse>) => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.Certificates] }),
  })

  const createMutation = useMutation({
    mutationFn: api.certificates.createAsync,
    onError: (error: AxiosError<ApiErrorResponse>) => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.Certificates] }),
  })

  const reviewMutation = useMutation({
    mutationFn: (
      request: CertificateReviewRequest & {
        Subdomain?: string | null
      }
    ) =>
      api.certificates.reviewAsync(request, {
        headers: { 'X-Tenant-Subdomain': request.Subdomain ?? false },
      }),
    onError: (error: AxiosError<ApiErrorResponse>) => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.Certificates] }),
  })

  const deleteMutation = useMutation({
    mutationFn: (request: { Id: number; Subdomain?: string | null }) =>
      api.certificates.deleteAsync(request.Id, {
        headers: { 'X-Tenant-Subdomain': request.Subdomain ?? false },
      }),
    onError: (error: AxiosError<ApiErrorResponse>) => {},
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.Certificates] }),
  })

  const onReload = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.Certificates] })
    queryClient.invalidateQueries({ queryKey: [CertificatesQueryKeys.CertificateTypesAll] })
  }, [queryClient])

  const loading = useMemo(
    () =>
      certificatesQuery.isLoading ||
      certificatesQuery.isFetching ||
      updateMutation.isPending ||
      createMutation.isPending ||
      reviewMutation.isPending ||
      deleteMutation.isPending,
    [
      certificatesQuery.isLoading,
      certificatesQuery.isFetching,
      updateMutation.isPending,
      createMutation.isPending,
      reviewMutation.isPending,
      deleteMutation.isPending,
    ]
  )

  return useMemo(
    () => ({
      loading,
      certificates: certificatesQuery.data?.Certificates ?? [],
      totalCount: certificatesQuery.data?.TotalCount ?? 0,
      certificatesStatus: certificatesQuery.status,
      refetchCertificates: certificatesQuery.refetch,
      updateAsync: updateMutation.mutateAsync,
      createAsync: createMutation.mutateAsync,
      reviewAsync: reviewMutation.mutateAsync,
      deleteAsync: deleteMutation.mutateAsync,
      onReload,
    }),
    [
      loading,
      certificatesQuery.data,
      certificatesQuery.status,
      certificatesQuery.refetch,
      updateMutation.mutateAsync,
      createMutation.mutateAsync,
      reviewMutation.mutateAsync,
      deleteMutation.mutateAsync,
      onReload,
    ]
  )
}

export default useCertificates
